import dayjs from 'dayjs'
import { initStore } from '@/store'
import { v4 as uuid } from 'uuid'
import { captureException, getCurrentScope } from '@sentry/nextjs'
import { DeviceTypeEnum, IDownloadType, ILocation } from './enums/device'
import dsBridge from './dsbridge'
export const isServer = typeof window === 'undefined'

/** Set true in _app after mount so `getUuid()` matches SSR on the client’s first paint (avoids href hydration mismatches). */
let isClientHydrated = false
export const markClientHydrated = () => {
  isClientHydrated = true
}

export const formatDate = (value: string | number | null | undefined, format = 'DD MMM YYYY') => {
  return value ? dayjs(value).format(format) : ''
}

export const filterSpecDom = (str: any) => {
  const pTagReg = /<p.*?\/p>/g
  const imgReg = /<img.*?/g
  const pTagResult = str?.match(pTagReg)
  if (!pTagResult?.length) return str

  for (let i = 0; i < pTagResult.length; i++) {
    const item = pTagResult[i]
    if (!imgReg?.test(item)) {
      return item
    }
  }
  return null
}

export const filterPureSlug = (str: string) => {
  if (!str?.includes('?')) return str
  return str?.replace(/\?(.*)$/g, '')
}

export const isEmail = (email: string) => {
  const reg = /^[A-Za-z0-9]+([_\.][A-Za-z0-9]+)*@([A-Za-z0-9\-]+\.)+[A-Za-z]{2,6}$/
  return reg.test(email)
}

/** ------- */
export const isIDMobile = (mobile: number | string) => {
  const reg = /^(\()?(\+62|62|0)(\d{2,3})?\)?[ .-]?\d{2,4}[ .-]?\d{2,4}[ .-]?\d{2,4}/
  return reg.test(mobile.toString())
}

/** ---------- */
export const isFromMobileFn = (userAgent = '') => {
  if (
    userAgent?.match(
      /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i,
    )
  ) {
    return true
  } else {
    return false
  }
}

export const setId = (id: number) => {
  initStore().ConfigStore.setTagId(id)
}

export const author = (author: any[]) => {
  return author?.length ? author[0]?.name : 'KreditPintar'
}

export const setCustomHeaders = (req: any, res: any, key: string) => {
  const matchTag = req.headers?.['if-none-match']
  let statusCode = 200
  const cache_key = `${key}_cache`
  if (getCustomCookie(cache_key, req.cookies)) {
    statusCode = 304
  } else {
    res.setHeader('Set-Cookie', [`${cache_key}=true; Max-Age=${3600 * 9}; HttpOnly=true`])
  }
  if (matchTag) {
    res.statusCode = statusCode
    res.setHeader('ETag', matchTag)
  }
  res.setHeader('Cache-Control', 'public, s-maxage=3600', 'stale-while-revalidate=7200')
}

export const getCustomCookie = (key: string, cookies: any) => {
  const cookie = cookies?.[key]
  if (cookie) {
    return JSON.parse(cookie)
  }
  return null
}

export const filterImg = (str: string) => {
  const imgReg = /<img.*?(?:>|\/>)/i
  if (!str?.match(imgReg)) return ''
  const srcReg = /src=[\'\"]?([^\'\"]*)[\'\"]?/i
  const result = str?.match(srcReg) ? str?.match(srcReg)[1] : ''
  return result
}

export const hasOldImage = (str: string) => {
  const img_slice = [
    'www.kreditpintar.com/wp-content',
    'www.kreditpintar.com/wp-json',
    'loan.kreditpintar.com/blog/wp-content',
  ]
  return img_slice.some((item) => str.includes(item))
}

export const filterOldImage = (obj: any) => {
  const str = JSON.stringify(obj)
  const flag = hasOldImage(str)
  if (!flag) return JSON.parse(str)
  const reg1 = /https\:\/\/i0\.wp\.com\/www\.kreditpintar\.com(.*?)ssl=1/g
  const reg2 = /https\:\/\/www\.kreditpintar\.com\/wp-(.*?)(.jpg|.png)/g
  const result = str
    .replace(reg1, ' https://static.kreditpintar.net/k8s-kp-website/images/common/dot.gif')
    .replace(reg2, '')
  return JSON.parse(result)
}

export const fetchCategory = (category: any[], type: 'name' | 'slug' = 'slug') => {
  return category?.[0]?.[0]?.[type] ? category[0][0][type] : ''
}

export const randomSort = () => {
  return Math.random() > 0.5 ? -1 : 1
}

export const getUuid = () => {
  if (isServer) return ''
  if (!isClientHydrated) return ''
  if (!getItem('uuid')) {
    setItem('uuid', uuid())
  }
  return getItem('uuid')
}

/**
 * -------- localStorage.setItem()
 * @param key --
 * @param values --
 * @param expires ----,----, --
 */
export const setItem = (key: string, values: any, expires?: number) => {
  if (isServer || !isSupportLocalStorage()) return
  if (expires) {
    const data = { values, expires, _start_: +new Date() }
    // ---------，---- trycatch
    localStorage?.setItem(key, JSON.stringify(data))
  } else {
    const type = Object.prototype.toString.call(values).slice(8, -1)
    if (type === 'Object' || type === 'Array') {
      localStorage?.setItem(key, JSON.stringify(values))
    } else {
      localStorage?.setItem(key, values)
    }
  }
}

/**
 * ----------
 * @param key --
 */
export const getItem = (key: string) => {
  if (isServer || !isSupportLocalStorage()) return null
  let data: any = localStorage?.getItem(key)
  try {
    data = JSON.parse(data)
  } catch (error) {
    data = data
  }
  if (data?._start_) {
    if (+new Date() - data._start_ > data.expires) {
      removeItem(key)
      return null
    } else {
      return data.values
    }
  } else {
    return data
  }
}

/**
 * ----------
 * @param key --
 */
export const removeItem = (key: string) => {
  if (isServer || !isSupportLocalStorage()) return
  try {
    localStorage?.removeItem(key)
  } catch (error) {
    console.log(error)
  }
}

export const resolveURL = (url: string): string => {
  const domain = process.env.NEXT_PUBLIC_GROWTH_HOST_BACKUP || process.env.NEXT_PUBLIC_GROWTH_HOST
  return `${
    domain
  }${url}?advertising-channel=OFFICIAL2021&utm_source=OfficialWebsite&utm_medium=default&utm_campaign=OfficialWebsite&utm_term=OfficialWebsite&cid=${getUuid()}`
}

export const isApp = () => {
  if (isServer) return
  const userAgent = window.navigator.userAgent
  return userAgent.includes('AtomeIdApp') || userAgent.includes('PINTAR_ANDROID') || userAgent.includes('PINTAR_IOS')
}

export function formatTimestamp(value: string | number, format = 'DD-MM-YYYY HH:mm:ss') {
  if (!value) {
    return '-'
  }
  try {
    return dayjs(value).format(format)
  } catch (error) {
    console.error(error)
    return '-'
  }
}

export const shimmer = (w, h) => `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#eee" offset="20%" />
      <stop stop-color="#ddd" offset="50%" />
      <stop stop-color="#eee" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="#eee" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="2s" repeatCount="indefinite"  />
</svg>`

export const toBase64 = (str) =>
  typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str)

/**
 * 热搜标签展示规则
 * @param result data resource
 */
export const filterHotTags = (data: any) => {
  const allTags = data.sort(() => {
    return 0.5 - Math.random()
  })
  // 热搜标签
  let toTopPostTagList = data.slice(20, 30)
  if (toTopPostTagList.length < 10) {
    // 热搜标签不够10个 从全部标签里取补
    toTopPostTagList = toTopPostTagList.concat(allTags.splice(0, 10 - toTopPostTagList.length))
  } else {
    // 热搜标签够10个 取前10个
    toTopPostTagList = toTopPostTagList.splice(0, 10)
  }
  return toTopPostTagList.concat(allTags).splice(0, 20)
}

export const shareIconList = () => {
  const locationRef = isServer ? '' : location.href
  return [
    {
      alt: 'facebook',
      url: `https://www.facebook.com/sharer/sharer.php?u=${locationRef}`,
      src: '/images/home/facebook.png',
    },
    {
      alt: 'instagram',
      url: `https://www.instagram.com/kreditpintar/`,
      src: '/images/home/photo.png',
    },
    {
      alt: 'twitter',
      url: `https://twitter.com/intent/tweet?url=${locationRef}`,
      src: '/images/home/twitter.png',
    },
  ]
}

export const getABTestResult = () => {
  if (getItem('abtest')) return getItem('abtest')
  const uuid = getUuid()
  if (!uuid) return 'A'
  const flag = uuid.charCodeAt(uuid.length - 1) % 2 === 0
  const branch = flag ? 'A' : 'B'
  setItem('abtest', branch)
  return branch
}

export const getPathUrl = (url: string) => {
  try {
    if (url?.indexOf('?') >= 0) {
      const path = url?.split('?')[0]
      return path
    }
    return url
  } catch (error) {
    console.error(error)
    return url
  }
}

/**
 * 手动控制异步导入
 * @param url
 */
export const loadScript = (url: string) => {
  const script = document.createElement('script')
  script.src = url
  script.async = true
  document.body.appendChild(script)
}

/**
 * 过滤需要的数据
 * @param url
 */
export const filterPostsData = (data: any) => {
  const temp = filterOldImage(data)
  return temp?.map((item) => {
    return {
      ...item,
      image: filterImg(item?.content?.rendered),
      _embedded: filterEmbeddedData(item?._embedded),
      _links: null,
      content: null,
    }
  })
}

const filterEmbeddedData = (data: any) => {
  if (!data) return {}
  return {
    author: data.author.map((item) => {
      return { name: item.name }
    }),
    'wp:term': twoArrayTraverse(data?.['wp:term']),
  }
}

const twoArrayTraverse = (arr: any[]) => {
  return arr.map((item) => {
    return item.map((t) => {
      return {
        id: t.id,
        link: t.link,
        name: t.name,
        slug: t.slug,
      }
    })
  })
}

/** 过滤置顶数据 */
export const filterTopPostsData = (data: any) => {
  return data?.map((item) => {
    return {
      ...item,
      _embedded: filterEmbeddedData(item?._embedded),
      _links: null,
      content: null,
    }
  })
}

/** sentry global config */
export const setGlobalConfig = () => {
  getCurrentScope().setTag('uuid', getItem('uuid'))
}

export const isSupportLocalStorage = () => {
  try {
    window.localStorage
    return true
  } catch (error) {
    captureException('not supported localStorage')
    return false
  }
}

export const isIOS = () => {
  return !isServer && /macintosh|mac os x/i.test(window.navigator.userAgent)
}

export const isHuaWei = () => {
  return !isServer && /HUAWEI/i.test(window.navigator.userAgent)
}

export const playPath = (downloadType: IDownloadType, linkPageName?: string, location?: ILocation) => {
  switch (downloadType) {
    case IDownloadType.IOS:
      switch (location) {
        case ILocation.EDUCATION:
          return `https://kreditpintar.onelink.me/GIv1/OWIOSEDU`
        case ILocation.FAQ:
          return `https://kreditpintar.onelink.me/GIv1/OWIOSFAQ`
        case ILocation.HOME:
        default:
          return `https://kreditpintar.onelink.me/GIv1/OWIOS`
      }

    case IDownloadType.HUAWEI:
      return `https://appgallery.huawei.com/#/app/C101093237`
    case IDownloadType.GOOGLE_PLAY:
      switch (location) {
        case ILocation.EDUCATION:
          return `https://kreditpintar.onelink.me/PVuW/OWAndroidEDU`
        case ILocation.FAQ:
          return `https://kreditpintar.onelink.me/PVuW/OWAndroidFAQ`
        case ILocation.HOME:
        default:
          return `https://kreditpintar.onelink.me/PVuW/OWAndroid`
      }
    default:
      return `https://kreditpintar.onelink.me/PVuW/OWAndroid`
  }
}

export const goToUrl = (url: string) => {
  if (window.dkBridge) {
    window.dkBridge?.openUrlInNewWindow?.(url)
  } else {
    dsBridge.call('openUrlInNewWindow', { url })
  }
}

export const getClientEnv = () => {
  if (window.navigator.userAgent.includes('PINTAR_IOS')) return DeviceTypeEnum.IOS
  else if (window.navigator.userAgent.includes('PINTAR_ANDROID') || window.navigator.userAgent.includes('AtomeIdApp'))
    return DeviceTypeEnum.ANDROID
  return DeviceTypeEnum.H5
}

export const parseJSON = (data: string) => {
  try {
    return JSON.parse(data)
  } catch (error) {
    console.error('Failed to parse JSON:', error)
    return null
  }
}
